/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { KeyboardEvent } from 'react'

interface ProgressPointMarkerProps {
  /** Injected by Victory: pixel position and the point's datum. */
  x?: number
  y?: number
  datum?: { x: number }
  selectedIndex: number | null
  onSelect: (index: number) => void
  /** Accessible name for the point (the readout text). */
  labelFor: (index: number) => string
  /** Fill of the point — its line's colour. */
  color: string
  /**
   * In the Tab order. Off for every line but the first when several share the
   * chart, so each period is one tab stop; a click still selects it.
   */
  focusable?: boolean
  /**
   * Hover / keyboard-focus tracking for the value tooltip: the point's period
   * index and pixel position while hovered, `null` when it's left.
   */
  onHoverChange: (hover: PointHover | null) => void
}

export interface PointHover {
  index: number
  /** Position in chart (viewBox) units, as Victory hands it to the point. */
  x: number
  y: number
}

const POINT_RADIUS = 5
const SELECTED_POINT_RADIUS = 7

/**
 * Victory `dataComponent` for the trend line's points: a focusable SVG circle
 * so a point can be selected by click, Tab focus, or Enter/Space.
 */
const ProgressPointMarker = ({
  x,
  y,
  datum,
  selectedIndex,
  onSelect,
  labelFor,
  color,
  focusable = true,
  onHoverChange,
}: ProgressPointMarkerProps) => {
  'use memo'

  if (x === undefined || y === undefined || !datum) {
    return null
  }

  const index = datum.x
  const isSelected = index === selectedIndex
  const select = () => onSelect(index)
  const showHover = () => onHoverChange({ index, x, y })
  const hideHover = () => onHoverChange(null)
  const handleFocus = () => {
    select()
    showHover()
  }

  const handleKeyDown = (event: KeyboardEvent<SVGCircleElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      select()
    }
  }

  return (
    <circle
      cx={x}
      cy={y}
      r={isSelected ? SELECTED_POINT_RADIUS : POINT_RADIUS}
      tabIndex={focusable ? 0 : -1}
      role='button'
      aria-label={labelFor(index)}
      aria-pressed={isSelected}
      onClick={select}
      onFocus={handleFocus}
      onBlur={hideHover}
      onMouseEnter={showHover}
      onMouseLeave={hideHover}
      onKeyDown={handleKeyDown}
      css={markerStyles(color)}
    />
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const markerStyles = (color: string) => css`
  fill: ${color};
  stroke: ${color};
  stroke-width: 1;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    stroke: ${colors.WHITE};
    stroke-width: 2;
  }
`

export default ProgressPointMarker
